import { useState } from 'react';
import { Download, Loader2, Clock, ArrowUpCircle, ArrowDownCircle, MapPin, Hash, Building2, User, Briefcase, Globe, FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { parseCollectContext, resolveCheckValue, formatCNPJ } from '../utils/formatters';
import { downloadCollectPDF } from '../utils/pdfExport';
import type { Collect, AgreementInfo } from '../types';

interface CollectDetailDialogProps {
  collect: Collect | null;
  agreementInfo: AgreementInfo | null;
  agreementUuid: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DetailRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function DetailRow({ icon: Icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <p className="truncate text-sm font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

export function CollectDetailDialog({
  collect,
  agreementInfo,
  agreementUuid,
  open,
  onOpenChange,
}: CollectDetailDialogProps) {
  const [pdfLoading, setPdfLoading] = useState(false);

  if (!collect) return null;

  const ctx = parseCollectContext(collect);
  const check = resolveCheckValue(collect);
  const contractor = agreementInfo?.contractor;
  const worker = agreementInfo?.worker;

  const dateLabel = format(parseISO(`${collect.date}T12:00:00`), "EEEE, dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  });

  function checkInfo(): { label: string; Icon: React.ElementType } {
    if (check === 'in') return { label: 'Entrada', Icon: ArrowUpCircle };
    if (check === 'out') return { label: 'Saída', Icon: ArrowDownCircle };
    return { label: 'Marcação', Icon: FileText };
  }

  const { label: checkTypeLabel, Icon: CheckIcon } = checkInfo();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90dvh] max-w-sm flex-col overflow-y-auto"
        aria-label="Detalhes da marcação"
      >
        <DialogHeader className="text-left">
          <DialogTitle>Comprovante de ponto</DialogTitle>
          <DialogDescription className="capitalize">{dateLabel}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Marcação */}
          <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
            <DetailRow icon={Clock} label="Horário" value={collect.time} />
            <DetailRow icon={CheckIcon} label="Tipo" value={checkTypeLabel} />
            <DetailRow icon={Hash} label="Identificador (NSR)" value={String(collect.nsr)} />
            {ctx?.timezone && (
              <DetailRow icon={Globe} label="Fuso horário" value={ctx.timezone} />
            )}
          </div>

          {/* Colaborador */}
          {(worker?.name || worker?.role || worker?.jobRole) && (
            <>
              <Separator />
              <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
                {worker.name && <DetailRow icon={User} label="Colaborador" value={worker.name} />}
                {(worker.role || worker.jobRole) && (
                  <DetailRow
                    icon={Briefcase}
                    label="Cargo"
                    value={(worker.role ?? worker.jobRole) as string}
                  />
                )}
              </div>
            </>
          )}

          {/* Empresa */}
          {(contractor?.name || contractor?.docValue) && (
            <>
              <Separator />
              <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
                {contractor.name && (
                  <DetailRow icon={Building2} label="Empresa" value={contractor.name} />
                )}
                {contractor.docValue && (
                  <DetailRow
                    icon={Hash}
                    label={contractor.docType ?? 'Documento'}
                    value={formatCNPJ(contractor.docValue)}
                  />
                )}
                {contractor.mainAddress?.label && (
                  <DetailRow
                    icon={MapPin}
                    label="Endereço"
                    value={contractor.mainAddress.label}
                  />
                )}
                {!contractor.mainAddress?.label &&
                  (contractor.mainAddress?.city || contractor.mainAddress?.region) && (
                    <DetailRow
                      icon={MapPin}
                      label="Localidade"
                      value={[contractor.mainAddress?.city, contractor.mainAddress?.region]
                        .filter(Boolean)
                        .join(' — ')}
                    />
                  )}
              </div>
            </>
          )}
        </div>

        <div className="pt-2">
          <Button
            className="w-full"
            disabled={pdfLoading || !agreementUuid}
            onClick={async () => {
              if (!agreementUuid) return;
              setPdfLoading(true);
              try {
                await downloadCollectPDF(agreementUuid, collect);
              } finally {
                setPdfLoading(false);
              }
            }}
            aria-label="Baixar comprovante em PDF"
          >
            {pdfLoading
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              : <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            }
            {pdfLoading ? 'Gerando PDF...' : 'Baixar comprovante (.pdf)'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
